import { jsx as _jsx } from "react/jsx-runtime";
import { isSupportedOS as isDesktopOS } from "@tv4/avod-web-player-device-capabilities";
import { forwardRef, } from "react";
import styled, { css } from "styled-components";
import { useLayoutSize } from "./providers/LayoutProvider";
import { colors } from "./theme/colors";
import { LayoutSize } from "./util/device";
export const DefaultButton = css `
  padding: 0;
  cursor: pointer;
  background: none;
  border: none;
  font-size: 1em;
  color: inherit;
`;
export const StartOverButtons = css `
  ${DefaultButton}
  display: flex;
  flex-direction: row;
  align-items: center;
  span {
    white-space: nowrap;
  }
`;
export const DefaultSvg = css `
  svg {
    width: var(--button-size);
    height: var(--button-size);
  }
`;
export const PlayerControlButton = styled.button `
  ${DefaultButton}
  ${DefaultSvg}
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: initial;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;
const ARROW_SIZE = 5;
export const ArrowDownBorder = styled.div `
  ${({ $position }) => {
    switch ($position) {
        case "left":
            return `left: ${2 * ARROW_SIZE}px;`;
        case "right":
            return `right: ${2 * ARROW_SIZE}px;`;
        default:
            return `left: calc(50% - ${ARROW_SIZE}px);`;
    }
}}

  ${({ $below }) => {
    return $below
        ? `bottom: 100%; border-bottom: ${ARROW_SIZE}px solid #000;`
        : `top: 100%; border-top: ${ARROW_SIZE}px solid #000;`;
}}

  position: absolute;
  width: 0;
  height: 0;
  border-left: ${ARROW_SIZE}px solid transparent;
  border-right: ${ARROW_SIZE}px solid transparent;
  pointer-events: none !important;
`;
export const HoverLabel = styled.div `
  ${({ $position }) => {
    switch ($position) {
        case "left":
            return `left: 0;`;
        case "right":
            return `right: 0;`;
        default:
            return `left: 50%; transform: translateX(-50%);`;
    }
}}

  ${({ $below }) => {
    return $below ? `top: 130%;` : `bottom: 130%;`;
}}

  position: absolute;
  font-size: 0.75em;
  background-color: black;
  color: white;
  padding: 6px 10px;
  border-radius: 3px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.1s ease-in;
  pointer-events: none !important;

  ${PlayerControlButton}:hover &, ${PlayerControlButton}:focus-visible & {
    opacity: 1;
  }

  &:hover,
  &:focus {
    opacity: 0;
  }
`;
const RefSection = forwardRef(({ children, ...props }, ref) => (_jsx("section", { ref: ref, ...props, children: children })));
export const SkinWrapper = styled(RefSection) `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  font-size: ${isDesktopOS() ? 1 : 0.8125}em;

  ${({ $usePauseAds = true }) => $usePauseAds &&
    css `
      display: grid;
      grid-template-rows: auto 1fr;
    `}

  overflow: hidden;
  z-index: 1;

  * {
    box-sizing: border-box;
  }
  ${({ $isActive }) => !$isActive &&
    css `
      cursor: none;
    `}
`;
export var GradientMode;
(function (GradientMode) {
    GradientMode["NONE"] = "none";
    GradientMode["TOP"] = "top";
    GradientMode["BOTTOM"] = "bottom";
    GradientMode["BOTH"] = "both";
})(GradientMode || (GradientMode = {}));
const getGradient = (gradientMode) => {
    switch (gradientMode) {
        case GradientMode.TOP:
            return css `
        background-image: linear-gradient(
          0deg,
          rgba(0, 0, 0, 0) 33.52%,
          rgba(0, 0, 0, 0.77) 100%
        );
      `;
        case GradientMode.BOTTOM:
            return css `
        background-image: linear-gradient(
          180deg,
          rgba(0, 0, 0, 0) 33.52%,
          rgba(0, 0, 0, 0.77) 100%
        );
      `;
        case GradientMode.BOTH:
            return css `
        background-image: linear-gradient(
            0deg,
            rgba(0, 0, 0, 0) 33.52%,
            rgba(0, 0, 0, 0.77) 100%
          ),
          linear-gradient(
            180deg,
            rgba(0, 0, 0, 0) 33.52%,
            rgba(0, 0, 0, 0.77) 100%
          );
      `;
        default:
            return;
    }
};
export function mainPadding(layoutSize) {
    const padding = {
        top: 1,
        right: 1,
        bottom: 0.5,
        left: 1,
    };
    if (isDesktopOS() &&
        [LayoutSize.MEDIUM, LayoutSize.LARGE].includes(layoutSize)) {
        padding.top = padding.bottom = 1;
        padding.left = padding.right = 2;
    }
    else if (layoutSize === LayoutSize.SMALLEST) {
        padding.top = padding.bottom = padding.left = padding.right = 0.25;
    }
    padding.all = `${padding.top}em ${padding.right}em ${padding.bottom}em ${padding.left}em`;
    return padding;
}
export const withMainPadding = (Component) => {
    return (props) => {
        const layoutSize = useLayoutSize();
        return _jsx(Component, { "$mainPadding": mainPadding(layoutSize), ...props });
    };
};
export const LabelBoundingContainer = styled.div `
  width: 100%;
  height: 100%;
  position: absolute;
`;
export const SkinContainer = withMainPadding(styled.div `
  position: absolute;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
  padding: ${({ $mainPadding }) => $mainPadding?.all ?? "0"};

  ${({ $gradientMode }) => getGradient($gradientMode)};

  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.25s;
  * {
    pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  }
  pointer-events: ${({ $buttonsEnabled }) => $buttonsEnabled ? "auto" : "none"};
  color: ${colors.white};
`);
export const PosterImage = styled.img `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: black;

  ${({ $blurred }) => ($blurred ? "filter: blur(33px)" : "")}
`;
export const FloatingBackButton = styled.div `
  position: absolute;
  top: 1em;
  left: ${isDesktopOS() ? 2 : 1}em;
`;
